import { functions, functionsAsia, db as firebaseDb } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { writeBatch, doc } from "firebase/firestore";
import { db as localDb } from "@/lib/db";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { useData } from "@/contexts/DataContext";
import { useToast } from "@/hooks/use-toast";
import { type BusinessProfile } from "@/lib/firebase";
import {
  Loader2,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import {
  SHOP_TYPES,
  type ShopType,
  getSeedProductsForShopType,
  getDefaultGstRateForShopType,
  shopTypeNeedsGst,
} from "@/lib/onboardingSeeds";

interface BusinessSetupProps {
  onComplete: () => void;
  initialData?: Partial<BusinessProfile>;
}

type SetupStep = "shopType" | "businessName" | "preview" | "completing";

const GSTIN_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z]\dZ[A-Z0-9]$/;

export default function BusinessSetupV2({ onComplete, initialData }: BusinessSetupProps) {
  const { user } = useAuth();
  const { saveBusinessProfile } = useData();
  const { toast } = useToast();

  const [step, setStep] = useState<SetupStep>("shopType");
  const [shopType, setShopType] = useState<ShopType | null>(null);
  const [businessName, setBusinessName] = useState(initialData?.businessName || "");
  const [gstMode, setGstMode] = useState<"none" | "gst" | null>(null);
  const [gstin, setGstin] = useState(initialData?.gstin || "");
  const [skippedQuiz, setSkippedQuiz] = useState(false);

  const triggerHaptic = async () => {
    try {
      const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch {}
  };

  const goShopType = (id: ShopType) => {
    setShopType(id);
    triggerHaptic();
    setStep("businessName");
  };

  const skipQuiz = () => {
    setSkippedQuiz(true);
    setShopType("other");
    setStep("businessName");
  };

  const submitBusinessName = () => {
    const trimmed = businessName.trim();
    if (!trimmed) {
      toast({ title: "Please enter your shop name", variant: "destructive" });
      return;
    }
    if (gstMode === null) {
      toast({ title: "Pick GST or No GST to continue", variant: "destructive" });
      return;
    }
    if (gstMode === "gst" && !GSTIN_REGEX.test(gstin.toUpperCase())) {
      toast({ title: "GSTIN looks incorrect (15 chars)", variant: "destructive" });
      return;
    }
    setBusinessName(trimmed);
    triggerHaptic();
    setStep("preview");
  };

  useEffect(() => {
    if (step === "preview") {
      const t = setTimeout(() => {
        try {
          confetti({
            particleCount: 80,
            spread: 60,
            origin: { y: 0.35 },
            colors: ["#8B1A1A", "#f59e0b", "#10b981"],
          });
        } catch {}
      }, 250);
      return () => clearTimeout(t);
    }
  }, [step]);

  const handleFinalSubmit = async () => {
    if (!user || !shopType) return;
    setStep("completing");
    const isGstOn = gstMode === "gst";
    const profile: BusinessProfile = {
      ownerName: initialData?.ownerName || user.displayName || "",
      businessName,
      businessAddress: initialData?.businessAddress || "",
      businessEmail: initialData?.businessEmail || user.email || "",
      businessPhone: initialData?.businessPhone || user.phoneNumber || "+91",
      businessWebsite: initialData?.businessWebsite || "",
      shopType,
      isGstEnabled: isGstOn,
      gstin: isGstOn ? gstin.toUpperCase() : "",
      phoneVerified: !!user.phoneNumber,
    };

    try {
      try {
        await user.getIdToken(true);
      } catch {}
      await saveBusinessProfile(profile);
    } catch (err: any) {
      const code = err?.code || "unknown";
      const msg = err?.message || "";
      console.error("[V2] saveBusinessProfile failed:", code, msg);
      // Retry once after a 1.2s pause (handles transient auth-state warm-up after fresh sign-in)
      try {
        await new Promise((r) => setTimeout(r, 1200));
        await user.getIdToken(true);
        await saveBusinessProfile(profile);
      } catch (retryErr: any) {
        const rcode = retryErr?.code || "unknown";
        const rmsg = retryErr?.message || "";
        console.error("[V2] saveBusinessProfile retry failed:", rcode, rmsg, retryErr);
        toast({
          title: "Couldn't save profile",
          description: `${rcode}: ${rmsg.slice(0, 120)}`,
          variant: "destructive",
        });
        setStep("preview");
        return;
      }
    }

    if (!skippedQuiz) {
      try {
        const seeds = getSeedProductsForShopType(shopType);
        const defaultRate = getDefaultGstRateForShopType(shopType);
        const batch = writeBatch(firebaseDb);
        const localRows: any[] = [];
        const baseTs = Date.now();
        seeds.forEach((s, idx) => {
          const id = `p_${baseTs}_${idx}_${Math.random().toString(36).slice(2, 9)}`;
          const productDoc = {
            id,
            name: s.name,
            rate: s.rate,
            salePrice: s.rate,
            costPrice: 0,
            category: s.category,
            unit: s.unit,
            stock: s.stock,
            barcode: `DH${baseTs}${idx}`,
            hsn: s.hsn || "",
            gstRate: isGstOn ? (s.gstRate ?? defaultRate) : 0,
            taxType: isGstOn ? (s.taxType ?? "cgst-sgst") : "none",
            gstInclusive: false,
            isFavorite: true,
            isFromExternal: false,
          };
          batch.set(doc(firebaseDb, "users", user.uid, "products", id), productDoc);
          localRows.push({ ...productDoc, syncStatus: "synced" as const });
        });
        await batch.commit();
        await localDb.products.bulkPut(localRows);
      } catch (err) {
        console.error("[V2] Seeding products failed (non-fatal):", err);
      }
    }

    const welcomeTasks: Promise<unknown>[] = [];
    if (profile.businessEmail) {
      const sendWelcome = httpsCallable(functionsAsia, "triggerWelcomeEmail");
      welcomeTasks.push(
        sendWelcome({
          toEmail: profile.businessEmail,
          businessName: profile.businessName,
          ownerName: profile.ownerName || profile.businessName,
        }).catch(() => {}),
      );
    }
    if (profile.businessPhone) {
      const sendWelcomeWA = httpsCallable(functions, "triggerWelcomeWhatsApp");
      welcomeTasks.push(
        sendWelcomeWA({
          phoneNumber: profile.businessPhone,
          businessName: profile.businessName,
        }).catch(() => {}),
      );
    }
    await Promise.race([
      Promise.allSettled(welcomeTasks),
      new Promise((resolve) => setTimeout(resolve, 5000)),
    ]);

    onComplete();
  };

  const stepVariants = {
    enter: { x: 50, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: -50, opacity: 0 },
  };

  const previewItems = shopType ? getSeedProductsForShopType(shopType).slice(0, 3) : [];
  const isGstOn = gstMode === "gst";
  const previewSubtotal = previewItems.reduce((sum, i) => sum + i.rate, 0);
  const defaultRate = shopType ? getDefaultGstRateForShopType(shopType) : 0;
  const previewTax = isGstOn
    ? previewItems.reduce(
        (sum, i) => sum + (i.rate * (i.gstRate ?? defaultRate)) / 100,
        0,
      )
    : 0;
  const previewTotal = previewSubtotal + previewTax;

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-GB");
  const timeStr = now.toLocaleTimeString("en-US", { hour12: false });
  const previewOrderId = "DEMO0001";

  const renderStep = () => {
    switch (step) {
      case "shopType":
        return (
          <motion.div
            key="shopType"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="space-y-6"
          >
            <div className="flex justify-center mb-4">
              <Logo size="md" />
            </div>
            <div className="space-y-2 text-center">
              <p className="text-xs font-medium text-muted-foreground/80 uppercase tracking-wider">
                Step 1 of 2
              </p>
              <h2 className="text-2xl font-bold">What kind of shop is this?</h2>
              <p className="text-muted-foreground text-sm">
                Aapki dukaan kis tarah ki hai? — We'll set things up for you.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {SHOP_TYPES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => goShopType(s.id)}
                  className="flex flex-col items-center justify-center gap-1.5 p-4 h-24 rounded-2xl border-2 border-border bg-card hover:border-primary hover:bg-primary/5 active:scale-[0.97] transition-all"
                  data-testid={`shop-type-${s.id}`}
                >
                  <span className="text-3xl leading-none">{s.emoji}</span>
                  <span className="text-xs font-semibold text-foreground text-center leading-tight">
                    {s.labelEn}
                  </span>
                  <span className="text-[10px] text-muted-foreground leading-none">{s.labelHi}</span>
                </button>
              ))}
            </div>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={skipQuiz}
            >
              Skip — I'll set up myself
            </Button>
          </motion.div>
        );

      case "businessName":
        return (
          <motion.div
            key="businessName"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="space-y-6"
          >
            <div className="flex justify-center mb-4">
              <Logo size="md" />
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground/80 uppercase tracking-wider">
                Step 2 of 2
              </p>
              <h2 className="text-2xl font-bold">What's your shop called?</h2>
              <p className="text-muted-foreground text-sm">
                This shows on every bill you make.
              </p>
            </div>
            <div className="space-y-5">
              <Input
                autoFocus
                className="h-14 text-lg"
                placeholder="e.g. Nalhati Grocery"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />

              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  How do you want to bill?
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      setGstMode("none");
                      triggerHaptic();
                    }}
                    className={`p-4 rounded-2xl border-2 transition-all active:scale-[0.97] ${
                      gstMode === "none"
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card"
                    }`}
                    data-testid="gst-none"
                  >
                    <p className="text-sm font-semibold">Without GST</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Simple bills</p>
                  </button>
                  <button
                    onClick={() => {
                      setGstMode("gst");
                      triggerHaptic();
                    }}
                    className={`p-4 rounded-2xl border-2 transition-all active:scale-[0.97] ${
                      gstMode === "gst"
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card"
                    }`}
                    data-testid="gst-yes"
                  >
                    <p className="text-sm font-semibold">With GST</p>
                    <p className="text-xs text-muted-foreground mt-0.5">I have GSTIN</p>
                  </button>
                </div>
                {shopType && shopTypeNeedsGst(shopType) && gstMode === "none" && (
                  <p className="text-xs text-amber-600 mt-1">
                    Most {shopType} shops use GST — but you can change later.
                  </p>
                )}
              </div>

              <AnimatePresence initial={false}>
                {gstMode === "gst" && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <Input
                      className="h-14 text-base font-mono uppercase tracking-wider"
                      placeholder="15-digit GSTIN"
                      value={gstin}
                      onChange={(e) => setGstin(e.target.value.toUpperCase().slice(0, 15))}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <Button size="lg" className="w-full h-14 text-lg" onClick={submitBusinessName}>
                Continue <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => setStep("shopType")}
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Back
              </Button>
            </div>
          </motion.div>
        );

      case "preview":
        return (
          <motion.div
            key="preview"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="space-y-5"
          >
            <div className="text-center space-y-1">
              <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-semibold">
                <Sparkles className="h-3.5 w-3.5" /> Your shop is ready!
              </div>
              <h2 className="text-2xl font-bold">Here's your first bill preview</h2>
              <p className="text-muted-foreground text-sm">
                You can change colors, logo, footer anytime.
              </p>
            </div>

            {/* Receipt — mirrors RecordSuccessPopup's showPrintPreview JSX exactly */}
            <div className="flex justify-center">
              <div
                style={{
                  position: "relative",
                  filter:
                    "drop-shadow(0 4px 20px rgba(0,0,0,0.08)) drop-shadow(0 8px 40px rgba(0,0,0,0.06)) drop-shadow(0 1px 3px rgba(0,0,0,0.04))",
                }}
              >
              <div
                style={{
                  width: "240px",
                  background: "white",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "12px",
                  padding: "20px 14px",
                  color: "#000",
                  position: "relative",
                  clipPath:
                    "polygon(0% 10px, 2.5% 0px, 5% 10px, 7.5% 0px, 10% 10px, 12.5% 0px, 15% 10px, 17.5% 0px, 20% 10px, 22.5% 0px, 25% 10px, 27.5% 0px, 30% 10px, 32.5% 0px, 35% 10px, 37.5% 0px, 40% 10px, 42.5% 0px, 45% 10px, 47.5% 0px, 50% 10px, 52.5% 0px, 55% 10px, 57.5% 0px, 60% 10px, 62.5% 0px, 65% 10px, 67.5% 0px, 70% 10px, 72.5% 0px, 75% 10px, 77.5% 0px, 80% 10px, 82.5% 0px, 85% 10px, 87.5% 0px, 90% 10px, 92.5% 0px, 95% 10px, 97.5% 0px, 100% 10px, 100% calc(100% - 10px), 97.5% 100%, 95% calc(100% - 10px), 92.5% 100%, 90% calc(100% - 10px), 87.5% 100%, 85% calc(100% - 10px), 82.5% 100%, 80% calc(100% - 10px), 77.5% 100%, 75% calc(100% - 10px), 72.5% 100%, 70% calc(100% - 10px), 67.5% 100%, 65% calc(100% - 10px), 62.5% 100%, 60% calc(100% - 10px), 57.5% 100%, 55% calc(100% - 10px), 52.5% 100%, 50% calc(100% - 10px), 47.5% 100%, 45% calc(100% - 10px), 42.5% 100%, 40% calc(100% - 10px), 37.5% 100%, 35% calc(100% - 10px), 32.5% 100%, 30% calc(100% - 10px), 27.5% 100%, 25% calc(100% - 10px), 22.5% 100%, 20% calc(100% - 10px), 17.5% 100%, 15% calc(100% - 10px), 12.5% 100%, 10% calc(100% - 10px), 7.5% 100%, 5% calc(100% - 10px), 2.5% 100%, 0% calc(100% - 10px))",
                }}
              >

                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "13px",
                    marginBottom: "1px",
                  }}
                >
                  {businessName.toUpperCase()}
                </div>

                {isGstOn && gstin && (
                  <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "9px" }}>
                    GSTIN: {gstin}
                  </div>
                )}

                <div style={{ borderTop: "1px dashed #555", margin: "5px 0" }} />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "10px",
                  }}
                >
                  <span>DATE: {dateStr}</span>
                  <span>TIME: {timeStr.slice(0, 5)}</span>
                </div>
                <div style={{ fontSize: "10px", marginTop: "1px" }}>
                  ORDER #: {previewOrderId}
                </div>

                <div style={{ borderTop: "1px dashed #555", margin: "5px 0" }} />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: "bold",
                    fontSize: "10px",
                    marginBottom: "2px",
                  }}
                >
                  <span>Qty/Item</span>
                  <span>Total</span>
                </div>
                <div style={{ borderTop: "1px dashed #555", margin: "4px 0" }} />

                {previewItems.map((item, idx) => (
                  <div key={idx} style={{ marginBottom: "5px" }}>
                    <div style={{ fontWeight: "bold", fontSize: "10px", wordBreak: "break-word" }}>
                      {item.name.toUpperCase()}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                      <span>1 x {item.rate.toFixed(2)}</span>
                      <span>Rs.{item.rate.toFixed(2)}</span>
                    </div>
                    {isGstOn && item.hsn && (
                      <div style={{ fontSize: "8px", paddingLeft: "8px", color: "#555" }}>
                        HSN: {item.hsn}
                      </div>
                    )}
                  </div>
                ))}

                <div style={{ borderTop: "1px dashed #555", margin: "5px 0" }} />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: "bold",
                    fontSize: "10px",
                  }}
                >
                  <span>TOTAL</span>
                  <span>Rs.{previewSubtotal.toFixed(2)}</span>
                </div>

                {isGstOn && (
                  <>
                    <div style={{ borderTop: "1px dashed #555", margin: "4px 0" }} />
                    <div style={{ fontWeight: "bold", fontSize: "9px" }}>GST SUMMARY</div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "8px",
                        fontWeight: "bold",
                        marginTop: "2px",
                      }}
                    >
                      <span style={{ width: "30%" }}>HSN</span>
                      <span style={{ width: "25%", textAlign: "right" }}>Taxable</span>
                      <span style={{ width: "20%", textAlign: "right" }}>CGST</span>
                      <span style={{ width: "20%", textAlign: "right" }}>SGST</span>
                    </div>
                    <div style={{ borderTop: "1px dashed #aaa", margin: "2px 0" }} />
                    {previewItems
                      .filter((i) => i.hsn)
                      .map((item, idx) => {
                        const rate = item.gstRate ?? defaultRate;
                        const tax = (item.rate * rate) / 100;
                        const half = (tax / 2).toFixed(2);
                        return (
                          <div
                            key={idx}
                            style={{ display: "flex", justifyContent: "space-between", fontSize: "8px" }}
                          >
                            <span style={{ width: "30%" }}>{item.hsn}</span>
                            <span style={{ width: "25%", textAlign: "right" }}>{item.rate.toFixed(2)}</span>
                            <span style={{ width: "20%", textAlign: "right" }}>{half}</span>
                            <span style={{ width: "20%", textAlign: "right" }}>{half}</span>
                          </div>
                        );
                      })}
                  </>
                )}

                <div
                  style={{
                    borderTop: "2px solid #000",
                    borderBottom: "2px solid #000",
                    padding: "4px 0",
                    margin: "5px 0",
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: "bold",
                    fontSize: "13px",
                  }}
                >
                  <span>NET PAYABLE</span>
                  <span>Rs.{previewTotal.toFixed(2)}</span>
                </div>

                <div style={{ height: "5px" }} />

                <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "10px" }}>
                  THANK YOU FOR SHOPPING!
                </div>
                <div style={{ textAlign: "center", fontSize: "9px", marginTop: "1px" }}>
                  Visit again soon
                </div>

                <div style={{ height: "10px" }} />
                <div style={{ textAlign: "center", fontSize: "8px", color: "#0000cc" }}>
                  Generated by DailyHisaab
                </div>
              </div>
              </div>
            </div>

            <p className="text-center text-xs text-muted-foreground -mt-2">
              This is exactly how your printed receipt will look.
            </p>

            {!skippedQuiz && (
              <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-900">
                  <p className="font-semibold">
                    {previewItems.length}+ sample products added to your inventory
                  </p>
                  <p className="opacity-80">
                    Edit, delete, or add your own — bills work either way.
                  </p>
                </div>
              </div>
            )}

            <Button
              size="lg"
              className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                triggerHaptic();
                handleFinalSubmit();
              }}
            >
              Looks good — let's start! <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => setStep("businessName")}
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Edit shop name
            </Button>
          </motion.div>
        );

      case "completing":
        return (
          <div className="flex flex-col items-center justify-center space-y-4 py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-xl font-medium">Setting up your shop...</p>
            <p className="text-muted-foreground text-sm">Adding sample products & GST settings</p>
          </div>
        );
    }
  };

  const stepsList: SetupStep[] = ["shopType", "businessName", "preview"];

  return (
    <div className="min-h-screen bg-background flex flex-col p-6 max-w-lg mx-auto">
      <div className="flex-1 flex flex-col justify-center">
        <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
      </div>
      {step !== "completing" && (
        <div className="py-6 flex justify-center gap-2">
          {stepsList.map((s, i) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                stepsList.indexOf(step) >= i ? "w-8 bg-primary" : "w-2 bg-primary/20"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
